"use client";

import * as React from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowRight02Icon,
  Location05FreeIcons,
} from "@hugeicons/core-free-icons";
import { ResponsivePopover } from "../ui/responsive-popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command";
import { Button } from "../ui/button";
import { Typography } from "../ui/typography";
import type { City, CitySuggestion } from "@/types/city";
import { useRecentSearches } from "@/hooks/useRecentSearches";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useCities } from "@/lib/react-query/queries/references/use-cities";
import {
  CITY_AUTOCOMPLETE_MIN_LENGTH,
  useCityAutocomplete,
} from "@/lib/react-query/queries/search/use-city-autocomplete";

interface DestinationSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onSelect: (value: string) => void; // This will receive slug
  isMobile: boolean;
  trigger: React.ReactNode;
}

function formatSuggestion(city: CitySuggestion): string {
  return city.provinceName ? `${city.name} - ${city.provinceName}` : city.name;
}

export function DestinationSearchModal({
  open,
  onOpenChange,
  search,
  onSearchChange,
  onSelect,
  isMobile,
  trigger,
}: DestinationSearchModalProps) {
  const { recentSearches, addRecentSearch } = useRecentSearches();
  const { data: citiesResponse, isLoading, error } = useCities();

  // Extract cities array from the API response (popular + recent display names)
  const cities = citiesResponse?.data || [];

  // Popular cities - get from API data or fallback
  const popularCities = React.useMemo(() => {
    if (!cities.length) return [];

    // You can define popular city slugs and filter from API data
    const popularSlugs = [
      "Lavasan-Tehran",
      "Mashhad-RazaviKhorasan",
      "Kashan-Esfahan",
      "Shiraz-Fars",
      "Tabriz-EastAzerbaijan",
    ];

    return popularSlugs
      .map((slug) => cities.find((city) => city.slug === slug))
      .filter(Boolean) as City[];
  }, [cities]);

  const trimmedSearch = search.trim();
  const debouncedSearch = useDebouncedValue(trimmedSearch, 300);
  const canSearch = trimmedSearch.length >= CITY_AUTOCOMPLETE_MIN_LENGTH;
  const {
    suggestions,
    isFetching: isSuggesting,
    isError: isSuggestError,
  } = useCityAutocomplete(debouncedSearch);

  // Debounce settled on a searchable query — avoid flashing empty/error mid-type
  const debounceSettled = debouncedSearch === trimmedSearch;
  const showSuggestions =
    canSearch && debounceSettled && suggestions.length > 0;
  const showSuggestLoading =
    canSearch && (!debounceSettled || (isSuggesting && suggestions.length === 0));
  const hasNoResults =
    canSearch &&
    debounceSettled &&
    !isSuggesting &&
    !isSuggestError &&
    suggestions.length === 0;
  const showInitialState = !canSearch;

  // Get display name for recent searches
  const getDisplayNameForRecentSearch = (searchValue: string): string => {
    // Try to find city by slug first
    const city = cities.find((c: City) => c.slug === searchValue);
    if (city) {
      return city.name;
    }
    // If not found, return the search value as is (could be province name)
    return searchValue;
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleCommandKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Keep cmdk / drawer chrome from hijacking keys during IME composition
    // (Persian and other non-Latin input methods).
    event.stopPropagation();
  };

  const mobileSearchHeader = isMobile ? (
    <div className="relative">
      <Button
        onClick={handleClose}
        variant="ghost"
        className="absolute top-1/2 -translate-y-1/2 right-2 z-10 bg-background gap-3"
        aria-label="بستن"
      >
        <HugeiconsIcon
          icon={ArrowRight02Icon}
          size={20}
          className="size-6"
          strokeWidth={1.5}
        />
      </Button>
      <CommandInput
        placeholder="جستجوی شهر یا استان"
        value={search}
        onValueChange={onSearchChange}
        onKeyDown={handleCommandKeyDown}
        className="mr-5"
        autoFocus={true}
      />
    </div>
  ) : null;

  const handleCitySelect = (citySlug: string) => {
    // Save to recent searches
    addRecentSearch(citySlug);
    // Call parent callback with slug
    onSelect(citySlug);
  };

  // Show loading state for the reference list (popular/recent labels)
  if (isLoading && showInitialState) {
    return (
      <ResponsivePopover
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        className="w-full! md:w-[400px]! p-0 md:rounded-[20px] overflow-hidden"
        direction="bottom"
      >
        <Command shouldFilter={false}>
          {mobileSearchHeader}
          <div className="flex items-center justify-center py-8">
            <Typography variant="captionRegular" color="black300">
              در حال بارگذاری...
            </Typography>
          </div>
        </Command>
      </ResponsivePopover>
    );
  }

  // Show error state only for the initial popular/recent load
  if (error && showInitialState) {
    return (
      <ResponsivePopover
        open={open}
        onOpenChange={onOpenChange}
        trigger={trigger}
        className="!w-full md:!w-[400px] p-0 md:rounded-[20px] overflow-hidden"
      >
        <Command shouldFilter={false}>
          {mobileSearchHeader}
          <div className="flex items-center justify-center py-8">
            <Typography variant="captionRegular" color="error">
              خطا در بارگذاری اطلاعات
            </Typography>
          </div>
        </Command>
      </ResponsivePopover>
    );
  }

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      className="w-full! md:w-[400px]! p-0 md:rounded-[20px] overflow-x-hidden md:max-h-96 overflow-y-auto"
    >
      <Command shouldFilter={false}>
        {mobileSearchHeader}

        {showSuggestLoading && (
          <div className="flex items-center justify-center py-8">
            <Typography variant="captionRegular" color="black300">
              در حال جستجو...
            </Typography>
          </div>
        )}

        {canSearch && debounceSettled && isSuggestError && (
          <div className="flex items-center justify-center py-8">
            <Typography variant="captionRegular" color="error">
              خطا در جستجو
            </Typography>
          </div>
        )}

        {/* No results state */}
        {hasNoResults && <CommandEmpty>مقصدی یافت نشد</CommandEmpty>}

        {/* Initial state - show recent and popular cities */}
        {showInitialState && (
          <>
            {recentSearches.length > 0 && (
              <CommandGroup heading="جستجوهای اخیر شما">
                <div className="flex flex-wrap gap-2.5 p-2">
                  {recentSearches.map((recentSearch) => (
                    <Button
                      key={recentSearch}
                      type="button"
                      variant="outline"
                      onClick={() => handleCitySelect(recentSearch)}
                      className="rounded-[15px] text-xs h-10 font-normal px-[14px]"
                      size="sm"
                    >
                      {getDisplayNameForRecentSearch(recentSearch)}
                    </Button>
                  ))}
                </div>
              </CommandGroup>
            )}

            {popularCities.length > 0 && (
              <CommandGroup heading="شهرهای پرطرفدار">
                <div className="flex flex-wrap gap-2.5 p-2">
                  {popularCities.map((city) => (
                    <Button
                      key={city?.id}
                      type="button"
                      variant="outline"
                      onClick={() => handleCitySelect(city?.slug)}
                      className="rounded-[15px] text-xs h-10 font-normal px-[14px]"
                      size="sm"
                    >
                      {city?.name}
                    </Button>
                  ))}
                </div>
              </CommandGroup>
            )}
          </>
        )}

        {/* Autocomplete results */}
        {showSuggestions && (
          <CommandGroup heading="شهرها">
            {suggestions.map((city) => (
              <CommandItem
                key={city.slug}
                value={city.name}
                onSelect={() => handleCitySelect(city.slug)}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex items-center justify-center size-9 bg-green-50 rounded-md">
                    <HugeiconsIcon
                      icon={Location05FreeIcons}
                      className="text-primary-500"
                      size={18}
                    />
                  </div>
                  <Typography variant="captionMedium" color="default">
                    {formatSuggestion(city)}
                  </Typography>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </Command>
    </ResponsivePopover>
  );
}
